import { useId, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileDropzone } from '../../../item-master/components/FileDropzone';
import { RESULTS_ACCEPT, validateResultsFile } from '../resultsFile';
import { ToolCard } from '../ToolCard';
import { MissingHeaderError } from './readUnloadedSpaInputs';
import {
  divisionLabel,
  useUnloadedSpas,
  type InputState,
} from './useUnloadedSpas';

/** What became of a dropped file: its name and line count, or why it was not read. */
function InputStatus<Line>({ state }: { state: InputState<Line> }) {
  const gt = useGT();
  if (state.status === 'empty') return null;
  let detail: ReactNode;
  if (state.status === 'reading') {
    detail = gt('Reading…', { context: 'a dropped workbook is being read' });
  } else if (state.status === 'read') {
    detail = gt('{count} lines', {
      count: state.lines.length.toLocaleString(),
      context: 'how many lines were read from a dropped workbook',
    });
  } else {
    detail = (
      <span className="text-destructive">
        {state.error instanceof MissingHeaderError
          ? gt('Not the expected file: no sheet has its headers.', {
              context:
                'a dropped workbook lacks the columns the ABB unloaded SPA report reads',
            })
          : gt('Could not read this file.', {
              context: 'a dropped workbook could not be opened',
            })}
      </span>
    );
  }
  return (
    <div className="flex min-w-0 gap-2 text-xs">
      <span className="truncate font-medium" title={state.fileName}>
        {state.fileName}
      </span>
      <span className="text-muted-foreground shrink-0">{detail}</span>
    </div>
  );
}

/**
 * ABB Unloaded SPAs: which of ABB's SPAs a Division has not loaded, and, for
 * a pasted list of its customers, which SPA each is missing from its account.
 */
export function UnloadedSpasCard() {
  const gt = useGT();
  const divisionLabelId = useId();
  const customersId = useId();
  const tool = useUnloadedSpas();
  const validate = (file: File) =>
    Promise.resolve(validateResultsFile(file, gt));
  const reading =
    tool.spaList.status === 'reading' || tool.loads.status === 'reading';

  return (
    <ToolCard
      fixedHeight={false}
      title={
        <T context="heading of the ABB unloaded SPA report card">
          ABB Unloaded SPAs
        </T>
      }
      description={
        <T context="explanation under the ABB unloaded SPA report card heading">
          Which ABB SPAs a Division has not loaded. Drop ABB’s SPA list and the
          Results Workbook, pick the Division, and optionally paste customers to
          check.
        </T>
      }
      action={
        <Button
          size="sm"
          disabled={!tool.ready}
          onClick={() => void tool.download()}
        >
          <DownloadSimpleIcon className="h-4 w-4" />
          {tool.building ? (
            <T context="the ABB unloaded SPA report is being built">
              Building…
            </T>
          ) : (
            <T context="button that downloads the ABB unloaded SPA report">
              Download report
            </T>
          )}
        </Button>
      }
    >
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-2">
          <FileDropzone
            inline
            accept={RESULTS_ACCEPT}
            disabled={reading}
            validate={validate}
            onFileAccepted={tool.dropSpaList}
            prompt={
              <T context="drop area for ABB's SPA list workbook">
                ABB SPA list (ABB SPAS CUSTOMER INFO ONLY)
              </T>
            }
          />
          <InputStatus state={tool.spaList} />
          <FileDropzone
            inline
            accept={RESULTS_ACCEPT}
            disabled={reading}
            validate={validate}
            onFileAccepted={tool.dropLoads}
            prompt={
              <T context="drop area for ABB's Results Workbook">
                Results Workbook (ALL - RESULTS - ABB)
              </T>
            }
          />
          <InputStatus state={tool.loads} />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span id={divisionLabelId}>
              <T context="label before the Division picker of the ABB unloaded SPA report">
                Division
              </T>
            </span>
            <Select
              value={tool.division ?? ''}
              onValueChange={(next) => {
                if (typeof next === 'string' && next !== '') {
                  tool.setDivision(next);
                }
              }}
            >
              <SelectTrigger
                className="h-8 w-52"
                disabled={tool.divisions.length === 0}
                aria-labelledby={divisionLabelId}
              >
                <SelectValue
                  placeholder={gt('Choose a Division', {
                    context:
                      'placeholder of the Division picker; the Results Workbook lists them',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {tool.divisions.map((letter) => (
                  <SelectItem key={letter} value={letter}>
                    {divisionLabel(letter)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor={customersId} className="text-sm">
            <T context="label of the optional customer list of the ABB unloaded SPA report">
              Customers to check (optional): name and sold-to number per line
            </T>
          </label>
          <Textarea
            id={customersId}
            className="min-h-28 font-mono text-xs"
            value={tool.customerText}
            onChange={(event) => tool.setCustomerText(event.target.value)}
            placeholder={gt(
              'ACKISON ELECTRIC\t33474\nALL CANADIAN ELECTRICAL\t45845',
              {
                context:
                  'example rows of the pasted customer list: a customer name, a tab, its sold-to number',
              },
            )}
          />
          <span className="text-muted-foreground text-xs">
            {tool.customerCount > 0
              ? gt('{count} customers', {
                  count: tool.customerCount,
                  context: 'how many customers were read from the pasted list',
                })
              : gt('Without a list, the report covers the whole Division.', {
                  context:
                    'the ABB unloaded SPA report without a pasted customer list',
                })}
          </span>
        </div>
      </div>
    </ToolCard>
  );
}
